import { useEffect, useState } from "react";
import { Tree, Car, House, AirplaneTilt, DeviceMobile } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { fmtNum } from "@/lib/format";

const ITEMS = [
  { key: "trees_required", label: "Trees required (annual)", icon: Tree, color: "text-eco" },
  { key: "cars_off_road", label: "Cars removed for 1 year", icon: Car, color: "text-sky2" },
  { key: "homes_powered", label: "Homes powered for 1 year", icon: House, color: "text-gold" },
  { key: "flights_avoided", label: "LHR–JFK roundtrips avoided", icon: AirplaneTilt, color: "text-risk" },
  { key: "smartphones_charged", label: "Smartphones charged", icon: DeviceMobile, color: "text-violet2" },
];

export default function Impact() {
  const { active } = useProjects();
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    if (!active) return;
    api.get(`/projects/${active.id}/impact`).then((r) => setImpact(r.data));
  }, [active]);

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Environmental Equivalents"
        title="Project Impact"
        subtitle="Convert tCO₂e into intuitive metrics for stakeholders and ESG reporting."
        kicker={`TOTAL · ${fmtNum(impact?.tCO2e, 1)} tCO₂e`}
        guideId="impact"
      />

      <section className="px-8 pt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {ITEMS.map((i) => {
          const Icon = i.icon;
          return (
            <div key={i.key} data-testid={`impact-${i.key}`} className="border border theme-border theme-surface p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{i.label}</div>
                  <div className="font-mono-data text-4xl theme-ink mt-3">{fmtNum(impact?.[i.key])}</div>
                </div>
                <Icon size={28} weight="duotone" className={i.color} />
              </div>
            </div>
          );
        })}
      </section>

      <section
        className="mx-8 mt-6 mb-12 relative overflow-hidden border border theme-border"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,17,21,0.65), rgba(15,17,21,0.85)), url('https://images.unsplash.com/photo-1723081797549-629f69cbb7cb?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-10 md:p-16 max-w-2xl">
          <div className="overline">Editorial · Field note</div>
          <p className="font-serif-display text-3xl md:text-4xl theme-ink mt-3 leading-tight">
            Every ton avoided here<br />
            <span className="text-gold">compounds elsewhere.</span>
          </p>
          <p className="text-sm theme-ink-muted mt-4 max-w-xl leading-relaxed">
            Embodied carbon is locked-in the day a structure is built. Reductions made now propagate
            across the asset&apos;s full lifecycle — through procurement, supply chains, and the
            communities downstream.
          </p>
        </div>
      </section>
    </div>
  );
}
